/*
 * inflora-variant-gallery.js  (Opcion C)
 *
 * Objetivo:
 *   En productos con la etiqueta 'galeria-variante-unica', la galeria se
 *   renderiza (desde Liquid) con: [foto de la variante] + [generales].
 *   Al cambiar de variante, NO reconstruimos la galeria ni tocamos el
 *   Swiper: solo cambiamos la imagen de la PRIMERA foto (la de la
 *   variante) por la de la nueva variante. El resto (generales) no cambia.
 *
 * Por que asi:
 *   Reconstruir slides o recrear el loop de Swiper rompe la instancia de
 *   Minimog (errores de loopFix/cssMode). Cambiar solo el src de una img
 *   no toca la estructura del slider, asi que es estable en desktop y
 *   mobile.
 *
 * Como sabe cual variante:
 *   Escucha el evento nativo de Minimog 'variant:changed'
 *   (document.dispatchEvent en variant-picker.js), que trae la variante
 *   en event.detail.variant, con featured_media / featured_image.
 */

(() => {
  if (window.__infloraVariantGalleryC) return;
  window.__infloraVariantGalleryC = true;

  const TARGET = '[data-single-variant-media="true"]';

  /*
   * Construye un srcset a partir de la URL base de Shopify, con los mismos
   * anchos que usa el tema. La URL de Shopify admite el parametro width.
   */
  function buildSrcset(baseUrl) {
    if (!baseUrl) return '';
    const widths = [493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946];
    const sep = baseUrl.includes('?') ? '&' : '?';
    // Quitamos cualquier width= previo para no duplicar.
    const clean = baseUrl.replace(/([?&])width=\d+/g, '$1').replace(/[?&]$/, '');
    return widths
      .map((w) => {
        const s = clean.includes('?') ? '&' : '?';
        return `${clean}${s}width=${w} ${w}w`;
      })
      .join(', ');
  }

  function withWidth(baseUrl, w) {
    if (!baseUrl) return '';
    const clean = baseUrl.replace(/([?&])width=\d+/g, '$1').replace(/[?&]$/, '');
    const s = clean.includes('?') ? '&' : '?';
    return `${clean}${s}width=${w}`;
  }

  /*
   * Obtiene la URL de la imagen destacada de la variante.
   */
  function getVariantImageUrl(variant) {
    if (!variant) return '';
    if (variant.featured_media && variant.featured_media.preview_image) {
      return variant.featured_media.preview_image.src;
    }
    if (variant.featured_image && variant.featured_image.src) {
      return variant.featured_image.src;
    }
    return '';
  }

  function getVariantAlt(variant) {
    if (!variant) return '';
    if (variant.featured_media && variant.featured_media.alt) {
      return variant.featured_media.alt;
    }
    if (variant.featured_image && variant.featured_image.alt) {
      return variant.featured_image.alt;
    }
    return variant.name || variant.title || '';
  }

  /*
   * Actualiza una <img> (y su contenedor .m-product-media) con la nueva
   * imagen de variante.
   */
  function updateImg(mediaEl, url, alt) {
    if (!mediaEl) return;

    const img = mediaEl.querySelector('img');
    if (img) {
      const full = withWidth(url, 1946);
      img.src = full;
      img.srcset = buildSrcset(url);
      if (alt) img.alt = alt;
    }

    // Minimog guarda la fuente grande para el zoom / lightbox aqui.
    const mediaData = mediaEl.matches('.m-product-media')
      ? mediaEl
      : mediaEl.querySelector('.m-product-media');
    if (mediaData) {
      mediaData.dataset.mediaSrc = withWidth(url, 1946);
      if (alt) mediaData.dataset.mediaAlt = alt;
    }
  }

  /*
   * En un slider con loop, el primer slide real tiene
   * data-swiper-slide-index="0"; sus clones comparten ese valor.
   * Actualizamos el slide real y cualquier clon, para que al deslizar el
   * loop no aparezca la imagen vieja.
   *
   * La galeria de thumbnails no tiene loop, asi que su primer slide es
   * simplemente el de indice 0.
   */
  function updateFirstImages(gallery, url, alt) {
    // --- Imagen grande (slider principal) ---
    const mainImages = gallery.querySelectorAll(
      '.m-product-media--slider__images .m-product-media--item'
    );

    mainImages.forEach((slide) => {
      const swiperIdx = slide.dataset.swiperSlideIndex;
      const idx = slide.dataset.index;
      // El slide real de la variante es index 0; con loop, los clones
      // llevan data-swiper-slide-index="0".
      if (swiperIdx === '0' || idx === '0') {
        const mediaEl = slide.querySelector('.m-product-media');
        updateImg(mediaEl || slide, url, alt);
      }
    });

    // --- Thumbnail correspondiente (primer thumb) ---
    const firstThumb = gallery.querySelector(
      '.m-product-media--slider__thumbnails .swiper-slide[data-index="0"] .m-product-media, ' +
      '.m-product-media--slider__thumbnails .swiper-slide:first-child .m-product-media'
    );
    if (firstThumb) {
      // Los thumbnails usan un ancho chico.
      const img = firstThumb.querySelector('img');
      if (img) {
        img.src = withWidth(url, 120);
        img.srcset = '';
        if (alt) img.alt = alt;
      }
    }
  }

  function handleVariantChange(variant) {
    if (!variant) return;

    const url = getVariantImageUrl(variant);
    if (!url) return;

    const alt = getVariantAlt(variant);

    document.querySelectorAll(TARGET).forEach((gallery) => {
      updateFirstImages(gallery, url, alt);
    });
  }

  /*
   * Neutralizamos el salto nativo de Minimog para estos productos.
   *
   * Minimog, al cambiar variante, hace:
   *   this.slider.slideToLoop(variant.featured_media.position - 1)
   * donde 'position' es el indice en la galeria COMPLETA original (1..N).
   * Como nosotros filtramos la galeria, esa posicion ya no corresponde y
   * haria saltar el slider a un slide equivocado o inexistente.
   *
   * Nuestra galeria filtrada SIEMPRE tiene la foto de la variante en el
   * indice 0, y ademas cambiamos su src in-place. Asi que forzamos que,
   * para las galerias target, el salto sea siempre a 0 (o simplemente se
   * quede donde esta). Parcheamos setActiveMediaForSlider.
   */
  function patchNativeSlider() {
    const GalleryClass = customElements.get('media-gallery');
    if (!GalleryClass) return;
    if (GalleryClass.prototype.__infloraPatchedC) return;

    const proto = GalleryClass.prototype;
    const original = proto.setActiveMediaForSlider;

    proto.setActiveMediaForSlider = function (variant) {
      // Solo intervenimos en productos con galeria de variante unica.
      if (this.dataset && this.dataset.singleVariantMedia === 'true') {
        // La foto de la variante es el slide 0 y su src ya se actualizo.
        // Llevamos el slider al inicio de forma segura, sin usar la
        // position original (que aqui no aplica).
        if (this.slider && typeof this.slider.slideToLoop === 'function') {
          try {
            this.slider.slideToLoop(0, 300);
          } catch (e) {
            try {
              this.slider.slideTo(0, 300);
            } catch (e2) {}
          }
        }
        return;
      }

      if (typeof original === 'function') {
        return original.call(this, variant);
      }
    };

    proto.__infloraPatchedC = true;
  }

  if (customElements.get('media-gallery')) {
    patchNativeSlider();
  } else {
    customElements.whenDefined('media-gallery').then(patchNativeSlider);
  }

  /*
   * Evento nativo de Minimog al cambiar variante.
   * variant-picker.js hace: document.dispatchEvent(
   *   new CustomEvent('variant:changed', { detail: { variant } })
   * )
   *
   * Nota de orden: actualizamos la imagen (src) ANTES de que el slider
   * intente moverse; ambos escuchan el mismo cambio, pero como cambiamos
   * el slide 0 in-place y el slider vuelve a 0, el resultado es correcto
   * sin importar el orden exacto.
   */
  document.addEventListener('variant:changed', (event) => {
    const variant = event.detail && event.detail.variant;
    handleVariantChange(variant);
  });
})();
